// Mounts the WebMCP tool sets. Rendering these components is what makes the
// page agent-operable; unmounting removes the tools again (AbortSignal).

import { useCallback, useMemo } from "react";
import { useRouter } from "@tanstack/react-router";
import { useWebMcpTools } from "@/lib/webmcp/use-webmcp";
import { createSiteTools } from "@/lib/webmcp/tools/site";
import { createLibraryTools } from "@/lib/webmcp/tools/library";
import type { LibraryToolDeps } from "@/lib/webmcp/tools/library";

// The router instance is stable for the app's lifetime, so tool sets built on
// top of it never churn — re-registering on every render would loop.
function useSiteNavigate() {
  const router = useRouter();
  return useCallback(
    (to: string) => {
      void router.navigate({ to, replace: false } as never);
    },
    [router],
  );
}

/** Site-wide tools — mounted once in the root component. */
export function WebMcpSiteTools() {
  const router = useRouter();
  const navigate = useSiteNavigate();

  const tools = useMemo(
    () =>
      createSiteTools({
        navigate,
        currentPath: () => router.state.location.pathname,
      }),
    [navigate, router],
  );
  useWebMcpTools(tools, "site");
  return null;
}


/** Library tools — mounted by library routes. */
export function WebMcpLibraryTools({
  applyFilters,
}: {
  applyFilters?: LibraryToolDeps["applyFilters"];
}) {
  const navigate = useSiteNavigate();
  const tools = useMemo(
    () => createLibraryTools({ navigate, applyFilters }),
    [navigate, applyFilters],
  );
  useWebMcpTools(tools, "library");
  return null;
}

/**
 * Gem-page tools: scoped to the gem currently on screen, so an agent can
 * answer "what is this?" and open the resource without knowing the slug.
 */
export function WebMcpGemTools({
  slug,
  name,
  url,
}: {
  slug: string;
  name: string;
  url: string;
}) {
  const tools = useMemo(
    () => [
      {
        name: "gem_current",
        title: "Read the gem on screen",
        description:
          "Return the full record of the library gem the user is currently viewing, including its canonical link.",
        inputSchema: { type: "object" as const, properties: {}, additionalProperties: false },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        execute: async () => {
          const res = await fetch(`/api/public/library/gems/${encodeURIComponent(slug)}`);
          const data = res.ok ? await res.json() : { slug, name, url };
          return {
            content: [{ type: "text" as const, text: `Viewing: ${name} (${url})` }],
            structuredContent: data,
          };
        },
      },
      {
        name: "gem_open_source",
        title: "Open this resource",
        description:
          "Open the external website of the gem currently on screen in a new tab. Ask the user first.",
        inputSchema: { type: "object" as const, properties: {}, additionalProperties: false },
        annotations: { readOnlyHint: false, openWorldHint: true },
        execute: () => {
          window.open(url, "_blank", "noopener,noreferrer");
          return { content: [{ type: "text" as const, text: `Opened ${url}` }] };
        },
      },
    ],
    [slug, name, url],
  );
  useWebMcpTools(tools, "gem");
  return null;
}
